// Configuración → Cuentas Alegra — Iter 294.e (Carlos, jun-2026)
// Mapeo familia del maestro → categoría de gasto Alegra + recategorización
// batch de bills atascadas en 5124 "Facturas por Recibir" (dry-run primero).
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Checkbox } from '../../components/ui/checkbox';
import { SearchableCombobox } from '../../components/ui/searchable-combobox';
import { AsignarFamiliasDialog } from './AsignarFamiliasDialog';
import { AsientosReclasificacionCard } from './AsientosReclasificacionCard';
import { fmtFecha } from '../../lib/utils';
import {
  BookOpen, RefreshCw, Save, Wand2, Search, PlayCircle,
  AlertTriangle, CheckCircle2, XCircle, Loader2, ListChecks, Download, Tags,
} from 'lucide-react';
import { toast } from 'sonner';

const fmtCLP = (n) => `$${Math.round(n || 0).toLocaleString('es-CL')}`;

const CLASIF_BADGE = {
  MA: 'bg-blue-100 text-blue-700',
  SE: 'bg-purple-100 text-purple-700',
  GA: 'bg-amber-100 text-amber-700',
};

function FuenteBadge({ entry }) {
  if (!entry?.category_id) return <span className="text-[10px] text-muted-foreground italic">sin mapear</span>;
  if (entry.auto) {
    return <Badge variant="outline" className="text-[10px] text-indigo-600 border-indigo-300">auto · {entry.fuente || 'sugerida'}</Badge>;
  }
  return <Badge variant="outline" className="text-[10px] text-emerald-700 border-emerald-300">manual</Badge>;
}

export default function CuentasAlegraPage() {
  const [categorias, setCategorias] = useState([]);
  const [familias, setFamilias] = useState([]);
  const [mapping, setMapping] = useState({});
  const [fallbacks, setFallbacks] = useState({});
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [sugiriendo, setSugiriendo] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [soloSinMapear, setSoloSinMapear] = useState(false);
  // recategorización
  const [estado, setEstado] = useState({ status: 'idle' });
  const [propuestas, setPropuestas] = useState([]);
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [soloAbiertas, setSoloAbiertas] = useState(false);
  const pollRef = useRef(null);

  const loadMapeo = useCallback(async () => {
    const [m, c] = await Promise.all([
      api.get('/contabilidad/cuentas-alegra/mapeo'),
      api.get('/contabilidad/cuentas-alegra/categorias'),
    ]);
    setFamilias(m.data.familias || []);
    setMapping(m.data.mapping || {});
    setFallbacks(m.data.fallbacks || {});
    setMeta({ updated_at: m.data.updated_at, updated_by: m.data.updated_by, n_cats: m.data.categorias_sincronizadas });
    setCategorias(c.data.categorias || []);
  }, []);

  const loadEstado = useCallback(async () => {
    const r = await api.get('/contabilidad/cuentas-alegra/recat/estado');
    setEstado(r.data || { status: 'idle' });
    return r.data;
  }, []);

  const loadPropuestas = useCallback(async () => {
    const r = await api.get('/contabilidad/cuentas-alegra/recat/propuestas?limit=2000');
    setPropuestas(r.data?.propuestas || []);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const est = await loadEstado();
        await loadMapeo();
        if (est && ['listo', 'completado', 'error'].includes(est.status)) loadPropuestas();
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Error cargando Cuentas Alegra');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadMapeo, loadEstado, loadPropuestas]);

  // polling mientras hay proceso en curso
  useEffect(() => {
    const activo = ['analizando', 'ejecutando'].includes(estado.status);
    if (activo && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        try {
          const est = await loadEstado();
          if (!['analizando', 'ejecutando'].includes(est.status)) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            loadPropuestas();
          }
        } catch { /* siguiente tick */ }
      }, 2500);
    }
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [estado.status, loadEstado, loadPropuestas]);

  const catItems = useMemo(
    () => categorias.map((c) => ({ value: String(c.id), label: c.name, subLabel: `#${c.id}${c.nature ? ' · ' + c.nature : ''}` })),
    [categorias],
  );

  const familiasFiltradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return familias.filter((f) => {
      if (soloSinMapear && mapping[f.familia]?.category_id) return false;
      if (q && !f.familia.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [familias, busqueda, soloSinMapear, mapping]);

  const nMapeadas = useMemo(() => familias.filter((f) => mapping[f.familia]?.category_id).length, [familias, mapping]);

  const setFamiliaCat = (familia, catId) => {
    setMapping((prev) => {
      const next = { ...prev };
      if (!catId) {
        delete next[familia];
      } else {
        const cat = categorias.find((c) => String(c.id) === String(catId));
        next[familia] = { category_id: String(catId), category_name: cat?.name || '', auto: false };
      }
      return next;
    });
  };

  const setFallbackCat = (key, catId) => {
    setFallbacks((prev) => {
      const next = { ...prev };
      if (!catId) {
        delete next[key];
      } else {
        const cat = categorias.find((c) => String(c.id) === String(catId));
        next[key] = { category_id: String(catId), category_name: cat?.name || '' };
      }
      return next;
    });
  };

  const handleSyncCategorias = async () => {
    setSyncing(true);
    try {
      const r = await api.post('/contabilidad/cuentas-alegra/sync-categorias');
      if (r.data?.ok) {
        toast.success(`${r.data.count} categorías sincronizadas desde Alegra`);
        await loadMapeo();
      } else {
        toast.error(r.data?.error || 'Error sincronizando');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error sincronizando categorías');
    } finally {
      setSyncing(false);
    }
  };

  const handleAutoSugerencias = async () => {
    setSugiriendo(true);
    try {
      const r = await api.post('/contabilidad/cuentas-alegra/mapeo/auto', {});
      if (r.data?.ok) {
        toast.success(`${r.data.sugeridas} sugerencias generadas (de ${r.data.total_familias} familias)`);
        await loadMapeo();
      } else {
        toast.error(r.data?.error || 'Error generando sugerencias');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error generando sugerencias');
    } finally {
      setSugiriendo(false);
    }
  };

  // Iter 385 — el mapeo debe coincidir con la cuenta del maestro de materiales (cta_alegra / malla)
  const handleAlinearMaestro = async () => {
    setSugiriendo(true);
    try {
      const prev = await api.post('/contabilidad/cuentas-alegra/mapeo/desde-maestro', { dry_run: true });
      const n = (prev.data.cambios?.length || 0) + (prev.data.nuevas?.length || 0);
      if (!n) { toast.info('El mapeo ya coincide con el maestro'); return; }
      const detalle = [...(prev.data.cambios || []), ...(prev.data.nuevas || [])].slice(0, 12)
        .map((c) => `• ${c.familia}: ${c.antes || 'sin mapear'} → ${c.ahora}`).join('\n');
      if (!window.confirm(`Se alinearán ${n} familia(s) con la cuenta del maestro (${prev.data.cambios?.length || 0} cambian, ${prev.data.nuevas?.length || 0} nuevas):\n\n${detalle}${n > 12 ? '\n…' : ''}\n\nSe guarda respaldo del mapeo actual. ¿Continuar?`)) return;
      const r = await api.post('/contabilidad/cuentas-alegra/mapeo/desde-maestro', {});
      toast.success(`Mapeo alineado con el maestro: ${r.data.cambios.length} cambios, ${r.data.nuevas.length} nuevas`);
      await loadMapeo();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error alineando con el maestro');
    } finally {
      setSugiriendo(false);
    }
  };

  const handleGuardar = async () => {
    setSaving(true);
    try {
      const r = await api.put('/contabilidad/cuentas-alegra/mapeo', { familias: mapping, fallbacks });
      toast.success(`Mapeo guardado (${r.data.familias_mapeadas} familias)`);
      await loadMapeo();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error guardando mapeo');
    } finally {
      setSaving(false);
    }
  };

  const handleAnalizar = async () => {
    try {
      const r = await api.post('/contabilidad/cuentas-alegra/recat/analizar', { anio });
      if (r.data?.ok) {
        toast.info(`Análisis ${anio} iniciado — no modifica nada en Alegra (dry-run)`);
        setPropuestas([]);
        loadEstado();
      } else {
        toast.error(r.data?.error || 'No se pudo iniciar el análisis');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error iniciando análisis');
    }
  };

  // Iter 386: el batch solo toma pendientes (sin resultado); las rechazadas por Alegra se reintentan una a una (▶ en la fila)
  const aplicablesPendientes = useMemo(
    () => propuestas.filter((p) => p.aplicable && !p.resultado && (!soloAbiertas || p.estado_bill === 'open')).length,
    [propuestas, soloAbiertas],
  );

  // Iter 385 — filtros del informe + asignación manual + ejecución individual
  const [filtroProp, setFiltroProp] = useState('todas');
  const [asignando, setAsignando] = useState(null);
  // Iter 386 — asignar familia a ítems de la OC desde el informe + acumulado de corridas
  const [famDialog, setFamDialog] = useState(null);
  const cerrarFamDialog = useCallback(() => setFamDialog(null), []);
  const acumulado = useMemo(() => {
    const n = (k) => propuestas.filter((p) => p.resultado === k).length;
    return { ok: n('ok'), parcial: n('parcial'), error: n('error'), ya: n('ya_recategorizada'), asiento: n('asiento') };
  }, [propuestas]);
  const handleFamiliasGuardadas = useCallback((nuevas) => {
    if (!nuevas.length) return;
    const porId = Object.fromEntries(nuevas.map((n) => [n.alegra_id, n]));
    setPropuestas((prev) => prev.map((x) => porId[x.alegra_id] || x));
  }, []);
  const propuestasFiltradas = useMemo(() => propuestas.filter((p) => {
    if (filtroProp === 'sin_propuesta') return !p.aplicable;
    if (filtroProp === 'parciales') return p.parcial || p.resultado === 'parcial';
    if (filtroProp === 'error') return p.resultado === 'error';
    if (filtroProp === 'pendientes') return p.aplicable && !p.resultado;
    if (filtroProp === 'ok') return p.resultado === 'ok';
    if (filtroProp === 'asiento') return p.resultado === 'asiento';
    return true;
  }), [propuestas, filtroProp]);

  const handleAsignarCuenta = async (p, catId) => {
    if (!catId) return;
    setAsignando(p.alegra_id);
    try {
      const r = await api.put(`/contabilidad/cuentas-alegra/recat/propuestas/${p.alegra_id}/cuenta`, { category_id: catId });
      setPropuestas((prev) => prev.map((x) => x.alegra_id === p.alegra_id
        ? { ...x, propuesta_id: r.data.propuesta_id, propuesta_name: r.data.propuesta_name, aplicable: true, parcial: false, resultado: null, error: null, fuente: 'manual' }
        : x));
      toast.success(`Folio ${p.folio}: cuenta ${r.data.propuesta_name} asignada — ejecuta para aplicarla en Alegra`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo asignar la cuenta');
    } finally {
      setAsignando(null);
    }
  };

  const handleEjecutarUna = async (p) => {
    if (!window.confirm(`Recategorizar en Alegra la bill ${p.folio} (${p.proveedor}) → ${p.propuesta_name || 'cuentas por línea'}?`)) return;
    try {
      const r = await api.post('/contabilidad/cuentas-alegra/recat/ejecutar', { alegra_ids: [p.alegra_id] });
      if (r.data?.ok) { toast.info(`Recategorizando ${p.folio}…`); loadEstado(); }
      else toast.error(r.data?.error || 'No se pudo iniciar');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error iniciando ejecución');
    }
  };

  const descargarInforme = () => {
    const cols = ['folio', 'proveedor', 'rut', 'fecha', 'total', 'estado_bill', 'oc', 'propuesta_name', 'fuente', 'resultado', 'error', 'items_sin_familia'];
    const esc = (v) => `"${String(Array.isArray(v) ? v.join(' | ') : (v ?? '')).replace(/"/g, '""')}"`;
    const csv = [cols.join(';'), ...propuestasFiltradas.map((p) => cols.map((c) => esc(c === 'fecha' ? fmtFecha(p[c]) : p[c])).join(';'))].join('\n');
    const url = URL.createObjectURL(new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = `informe_facturas_por_recibir_${filtroProp}.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 500);
  };

  const handleEjecutar = async () => {
    if (!window.confirm(
      `Se recategorizarán ${aplicablesPendientes} bill(s) en Alegra (5124 → cuenta propuesta).\n` +
      'Cada bill se verifica después del cambio: si el total cambia, el batch se ABORTA.\n\n¿Continuar?'
    )) return;
    try {
      const r = await api.post('/contabilidad/cuentas-alegra/recat/ejecutar', { solo_abiertas: soloAbiertas });
      if (r.data?.ok) {
        toast.info('Recategorización iniciada…');
        loadEstado();
      } else {
        toast.error(r.data?.error || 'No se pudo iniciar la ejecución');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error iniciando ejecución');
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const enProceso = ['analizando', 'ejecutando'].includes(estado.status);
  const resumen = estado.resumen;

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="cuentas-alegra-page">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-indigo-600" /> Cuentas Alegra
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Mapea cada familia del maestro de materiales a su cuenta de gasto en Alegra.
            Al contabilizar un DTE, cada ítem usa la cuenta de su familia — se acabó el default "Facturas por Recibir" (5124).
          </p>
        </div>
        <Button variant="outline" onClick={handleSyncCategorias} disabled={syncing} data-testid="btn-sync-categorias">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Sincronizando…' : `Sincronizar plan de cuentas (${meta.n_cats || 0})`}
        </Button>
      </div>

      {/* ───────────── Mapeo familia → categoría ───────────── */}
      <Card data-testid="card-mapeo-familias">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base flex items-center gap-2">
              <ListChecks className="w-4 h-4 text-indigo-600" />
              Mapeo Familia → Cuenta de gasto
              <Badge variant="secondary" className="text-[11px]">{nMapeadas}/{familias.length} mapeadas</Badge>
            </CardTitle>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleAlinearMaestro} disabled={sugiriendo} data-testid="btn-alinear-maestro">
                <BookOpen className="w-4 h-4 mr-1.5" /> Alinear con maestro
              </Button>
              <Button size="sm" variant="outline" onClick={handleAutoSugerencias} disabled={sugiriendo} data-testid="btn-auto-sugerencias">
                <Wand2 className="w-4 h-4 mr-1.5" /> {sugiriendo ? 'Generando…' : 'Sugerencias automáticas'}
              </Button>
              <Button size="sm" onClick={handleGuardar} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="btn-guardar-mapeo">
                <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Guardando…' : 'Guardar mapeo'}
              </Button>
            </div>
          </div>
          {meta.updated_at && (
            <p className="text-[11px] text-muted-foreground">
              Última edición: {new Date(meta.updated_at).toLocaleString('es-CL')} · {meta.updated_by}
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar familia…"
                className="pl-8 text-sm"
                data-testid="input-buscar-familia"
              />
            </div>
            <label className="flex items-center gap-1.5 text-xs cursor-pointer">
              <Checkbox checked={soloSinMapear} onCheckedChange={(v) => setSoloSinMapear(!!v)} data-testid="check-solo-sin-mapear" />
              Sólo sin mapear
            </label>
          </div>

          {categorias.length === 0 && (
            <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2.5" data-testid="aviso-sin-categorias">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              Aún no hay categorías sincronizadas. Presiona "Sincronizar plan de cuentas" primero.
            </div>
          )}

          <div className="border rounded max-h-[480px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted sticky top-0 z-10">
                <tr>
                  <th className="text-left px-2 py-1.5">Familia</th>
                  <th className="text-right px-2 py-1.5 w-20">Materiales</th>
                  <th className="text-center px-2 py-1.5 w-14">Clasif</th>
                  <th className="text-left px-2 py-1.5 w-[340px]">Cuenta de gasto Alegra</th>
                  <th className="text-left px-2 py-1.5 w-32">Origen</th>
                </tr>
              </thead>
              <tbody>
                {familiasFiltradas.map((f) => {
                  const entry = mapping[f.familia];
                  return (
                    <tr key={f.familia} className="border-t hover:bg-slate-50" data-testid={`fila-familia-${f.familia}`}>
                      <td className="px-2 py-1 font-medium">{f.familia}</td>
                      <td className="px-2 py-1 text-right font-mono text-muted-foreground">{f.n_materiales}</td>
                      <td className="px-2 py-1 text-center">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${CLASIF_BADGE[f.clasif] || 'bg-gray-100 text-gray-600'}`}>
                          {f.clasif}
                        </span>
                      </td>
                      <td className="px-2 py-1">
                        <SearchableCombobox
                          items={catItems}
                          value={entry?.category_id || ''}
                          onChange={(v) => setFamiliaCat(f.familia, v)}
                          placeholder="Buscar cuenta…"
                          compact
                          testid={`combo-cat-${f.familia}`}
                        />
                      </td>
                      <td className="px-2 py-1"><FuenteBadge entry={entry} /></td>
                    </tr>
                  );
                })}
                {familiasFiltradas.length === 0 && (
                  <tr><td colSpan={5} className="px-2 py-4 text-center text-muted-foreground italic">Sin familias que mostrar.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="pt-2 border-t">
            <p className="text-xs font-medium mb-1.5">Fallbacks por clasificación (cuando la familia no está mapeada)</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {[
                { key: 'MA', label: 'Materiales (MA)' },
                { key: 'SE', label: 'Servicios (SE)' },
                { key: 'GA', label: 'Gastos (GA)' },
                { key: 'general', label: 'General (sin material)' },
              ].map(({ key, label }) => (
                <div key={key}>
                  <label className="block text-[11px] text-muted-foreground mb-1">{label}</label>
                  <SearchableCombobox
                    items={catItems}
                    value={fallbacks[key]?.category_id || ''}
                    onChange={(v) => setFallbackCat(key, v)}
                    placeholder="Cuenta fallback…"
                    compact
                    testid={`combo-fallback-${key}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ───────────── Recategorización batch 5124 ───────────── */}
      <Card data-testid="card-recategorizacion">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <PlayCircle className="w-4 h-4 text-red-600" />
            Recategorizar bills en "Facturas por Recibir" (5124)
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Paso 1: <strong>Analizar</strong> descarga las bills del año desde Alegra y propone la cuenta <strong>según la familia de los ítems de la OC asociada</strong>
            (maestro de materiales → malla familia/subfamilia → cuenta elegida en la OC) <strong>sin modificar nada</strong>. Sin OC o con ítems sin familia → queda en el informe para asignar la cuenta a mano.
            Paso 2: revisas y ejecutas (todas o una por una). Las líneas cuyo ítem no tiene familia se mantienen en 5124. Cada bill se verifica tras el cambio; si el total varía, el batch se aborta.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <label className="block text-[11px] text-muted-foreground mb-1">Año</label>
              <Input
                type="number" min={2018} max={2030} value={anio}
                onChange={(e) => setAnio(parseInt(e.target.value, 10) || new Date().getFullYear())}
                className="w-28 text-sm" data-testid="input-anio-recat"
              />
            </div>
            <Button onClick={handleAnalizar} disabled={enProceso} variant="outline" data-testid="btn-analizar-recat">
              {estado.status === 'analizando'
                ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Analizando…</>
                : <><Search className="w-4 h-4 mr-1.5" /> Analizar (dry-run)</>}
            </Button>
            <label className="flex items-center gap-1.5 text-xs cursor-pointer pb-2.5">
              <Checkbox checked={soloAbiertas} onCheckedChange={(v) => setSoloAbiertas(!!v)} data-testid="check-solo-abiertas" />
              Sólo bills abiertas (open)
            </label>
            <Button
              onClick={handleEjecutar}
              disabled={enProceso || !['listo', 'completado'].includes(estado.status) || aplicablesPendientes === 0}
              className="bg-red-600 hover:bg-red-700"
              data-testid="btn-ejecutar-recat">
              {estado.status === 'ejecutando'
                ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Ejecutando…</>
                : <><PlayCircle className="w-4 h-4 mr-1.5" /> Ejecutar recategorización ({aplicablesPendientes})</>}
            </Button>
          </div>

          {enProceso && (
            <div className="text-xs bg-blue-50 border border-blue-200 rounded p-3" data-testid="recat-progreso">
              <div className="flex items-center gap-2 mb-1.5">
                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                <span className="font-medium">{estado.fase || 'Procesando…'}</span>
                {estado.total > 0 && <span className="text-muted-foreground">({estado.progreso || 0}/{estado.total})</span>}
              </div>
              {estado.total > 0 && (
                <div className="h-1.5 bg-blue-100 rounded overflow-hidden">
                  <div className="h-full bg-blue-600 transition-all" style={{ width: `${Math.min(100, ((estado.progreso || 0) / estado.total) * 100)}%` }} />
                </div>
              )}
            </div>
          )}

          {estado.status === 'error' && (
            <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-3" data-testid="recat-error">
              <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{estado.error}</span>
            </div>
          )}

          {resumen && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="recat-resumen">
              {[
                { label: `Bills ${estado.anio || ''}`, val: resumen.bills_anio },
                { label: 'En 5124', val: resumen.en_facturas_por_recibir, cls: 'text-red-700' },
                { label: 'Con propuesta', val: resumen.con_propuesta, cls: 'text-emerald-700' },
                { label: 'Parciales', val: resumen.parciales || 0, cls: 'text-amber-700' },
                { label: 'Sin propuesta', val: resumen.sin_propuesta, cls: 'text-red-700' },
                { label: 'Abiertas / Cerradas', val: `${resumen.abiertas} / ${resumen.cerradas}` },
                { label: 'Monto total', val: fmtCLP(resumen.monto_total) },
              ].map((c) => (
                <div key={c.label} className="border rounded p-2 text-center bg-white">
                  <p className={`text-sm font-bold font-mono ${c.cls || ''}`}>{c.val}</p>
                  <p className="text-[10px] text-muted-foreground">{c.label}</p>
                </div>
              ))}
            </div>
          )}

          {(estado.resumen_ejecucion || acumulado.ok + acumulado.parcial + acumulado.error > 0) && (
            <div className="flex items-center gap-3 flex-wrap text-xs bg-emerald-50 border border-emerald-200 rounded p-2.5" data-testid="recat-resumen-ejecucion">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span><strong>{acumulado.ok + acumulado.parcial}</strong> corregidas en Alegra ({acumulado.ok} completas · {acumulado.parcial} parciales, líneas sin familia siguen en 5124)</span>
              {acumulado.ya > 0 && <span className="text-amber-700">{acumulado.ya} ya estaban corregidas</span>}
              {acumulado.asiento > 0 && <span className="text-indigo-700">{acumulado.asiento} reclasificadas por asiento contable</span>}
              <span className="text-red-700">{acumulado.error} rechazadas por Alegra (pagadas o período cerrado)</span>
              {estado.resumen_ejecucion && (
                <span className="text-muted-foreground ml-auto" data-testid="recat-ultima-corrida">
                  última corrida: {estado.resumen_ejecucion.ok} ok · {estado.resumen_ejecucion.parcial || 0} parciales · {estado.resumen_ejecucion.error} rechazadas · {estado.resumen_ejecucion.omitidas} omitidas
                </span>
              )}
            </div>
          )}

          {propuestas.length > 0 && (
            <>
              <div className="flex items-center gap-2 flex-wrap text-xs" data-testid="recat-filtros">
                <span className="text-muted-foreground">Informe:</span>
                {[
                  ['todas', `Todas (${propuestas.length})`],
                  ['pendientes', `Pendientes (${propuestas.filter((p) => p.aplicable && !p.resultado).length})`],
                  ['sin_propuesta', `Sin propuesta (${propuestas.filter((p) => !p.aplicable).length})`],
                  ['parciales', `Parciales (${propuestas.filter((p) => p.parcial || p.resultado === 'parcial').length})`],
                  ['error', `Rechazadas por Alegra (${propuestas.filter((p) => p.resultado === 'error').length})`],
                  ['ok', `Corregidas (${propuestas.filter((p) => p.resultado === 'ok').length})`],
                  ['asiento', `Por asiento (${propuestas.filter((p) => p.resultado === 'asiento').length})`],
                ].map(([k, label]) => (
                  <button key={k} type="button" onClick={() => setFiltroProp(k)} data-testid={`recat-filtro-${k}`}
                    className={`rounded-full border px-2.5 py-1 transition-colors ${filtroProp === k ? 'bg-slate-800 text-white border-slate-800' : 'bg-white hover:bg-slate-50'}`}>
                    {label}
                  </button>
                ))}
                <Button size="sm" variant="ghost" className="ml-auto h-7 text-xs" onClick={descargarInforme} data-testid="btn-descargar-informe">
                  <Download className="w-3.5 h-3.5 mr-1" /> CSV ({propuestasFiltradas.length})
                </Button>
              </div>
              <div className="border rounded max-h-[520px] overflow-y-auto" data-testid="tabla-propuestas">
                <table className="w-full text-xs">
                  <thead className="bg-muted sticky top-0 z-10">
                    <tr>
                      <th className="text-left px-2 py-1.5">Folio</th>
                      <th className="text-left px-2 py-1.5">Proveedor</th>
                      <th className="text-left px-2 py-1.5">Fecha</th>
                      <th className="text-right px-2 py-1.5">Total</th>
                      <th className="text-center px-2 py-1.5">Estado</th>
                      <th className="text-left px-2 py-1.5">OC</th>
                      <th className="text-left px-2 py-1.5 min-w-[220px]">Cuenta propuesta / asignar</th>
                      <th className="text-left px-2 py-1.5">Fuente · detalle</th>
                      <th className="text-center px-2 py-1.5">Resultado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {propuestasFiltradas.map((p) => {
                      const editable = !enProceso && !['ok', 'ya_recategorizada', 'asiento'].includes(p.resultado);
                      const pendiente = p.aplicable && !['ok', 'parcial', 'ya_recategorizada', 'asiento'].includes(p.resultado);
                      return (
                        <tr key={p.alegra_id} className={`border-t ${!p.aplicable ? 'bg-red-50/40' : ''}`} data-testid={`propuesta-${p.folio}`}>
                          <td className="px-2 py-1 font-mono">{p.folio}</td>
                          <td className="px-2 py-1 truncate max-w-[180px]" title={p.proveedor}>{p.proveedor}</td>
                          <td className="px-2 py-1 whitespace-nowrap">{fmtFecha(p.fecha)}</td>
                          <td className="px-2 py-1 text-right font-mono">{fmtCLP(p.total)}</td>
                          <td className="px-2 py-1 text-center">
                            <Badge variant="outline" className={`text-[10px] ${p.estado_bill === 'open' ? 'text-blue-700 border-blue-300' : 'text-gray-500'}`}>
                              {p.estado_bill}
                            </Badge>
                          </td>
                          <td className="px-2 py-1 font-mono">{p.oc || <span className="text-muted-foreground">—</span>}</td>
                          <td className="px-2 py-1">
                            {editable ? (
                              <SearchableCombobox compact
                                items={catItems}
                                value={p.propuesta_id || ''}
                                onChange={(sel) => sel && handleAsignarCuenta(p, String(sel.value))}
                                placeholder={p.parcial ? 'Por línea (asignar p/ el resto)' : 'Asignar cuenta…'}
                                disabled={asignando === p.alegra_id}
                                testid={`combo-cuenta-${p.folio}`} />
                            ) : (p.propuesta_name || <span className="italic text-muted-foreground">—</span>)}
                          </td>
                          <td className="px-2 py-1 text-muted-foreground max-w-[260px]">
                            <div className="truncate" title={p.fuente}>{p.fuente}</div>
                            {p.items_sin_familia?.length > 0 && (
                              <div className="text-[10px] text-amber-700 truncate" title={p.items_sin_familia.join('\n')} data-testid={`sin-familia-${p.folio}`}>
                                sin familia: {p.items_sin_familia.slice(0, 2).join(', ')}{p.items_sin_familia.length > 2 ? ` (+${p.items_sin_familia.length - 2})` : ''}
                              </div>
                            )}
                            {p.error && <div className="text-[10px] text-red-700 truncate" title={p.error}>{p.error}</div>}
                            {editable && p.oc && (p.items_sin_familia?.length > 0 || !p.aplicable || p.parcial) && (
                              <button type="button" onClick={() => setFamDialog(p)} data-testid={`btn-familias-${p.folio}`}
                                className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-indigo-700 hover:text-indigo-900 hover:underline">
                                <Tags className="w-3 h-3" /> Asignar familia a ítems de la OC {p.oc}
                              </button>
                            )}
                          </td>
                          <td className="px-2 py-1 text-center whitespace-nowrap">
                            {p.resultado === 'ok' && <CheckCircle2 className="w-4 h-4 text-emerald-600 inline" />}
                            {p.resultado === 'parcial' && <span className="text-[10px] text-amber-700">parcial</span>}
                            {p.resultado === 'error' && <span className="text-red-600" title={p.error}><XCircle className="w-4 h-4 inline" /></span>}
                            {p.resultado === 'ya_recategorizada' && <span className="text-[10px] text-amber-600">ya OK</span>}
                            {p.resultado === 'asiento' && <span className="text-[10px] text-indigo-700" title="Reclasificada vía asiento contable">asiento</span>}
                            {!p.resultado && !p.aplicable && <span className="text-[10px] text-red-600">revisar</span>}
                            {pendiente && !enProceso && ['listo', 'completado'].includes(estado.status) && (
                              <button type="button" onClick={() => handleEjecutarUna(p)} title="Recategorizar solo esta bill"
                                className="ml-1 text-red-600 hover:text-red-800 align-middle" data-testid={`btn-ejecutar-una-${p.folio}`}>
                                <PlayCircle className="w-4 h-4 inline" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      <AsignarFamiliasDialog propuesta={famDialog} onClose={cerrarFamDialog} onSaved={handleFamiliasGuardadas} />
      <AsientosReclasificacionCard />
    </div>
  );
}
